import React, { useState, useMemo } from 'react';
import {
  gatePassService, purchaseService, invoiceService, expenseService, batchService, containerAgingService,
} from '../../services/financeService';
import { API_BASE } from '../../utils/apiUtils';
import CsvActions from '../common/CsvActions';
import PdfActions from '../common/PdfActions';
import './GDManagement.css'; // reuse the gd-* utility classes (cards, tables, buttons) already styled there

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtNum = (v) => v == null || v === '' ? '' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 });
const toDateOnly = (v) => v ? String(v).slice(0, 10) : null;

async function unwrapList(promise, ...paths) {
  const res = await promise;
  if (Array.isArray(res)) return res;
  for (const p of paths) {
    const v = p.split('.').reduce((o, k) => (o == null ? o : o[k]), res);
    if (Array.isArray(v)) return v;
  }
  return [];
}

// ─────────────────────────── Report catalog ───────────────────────────
// Each report is self-contained: how to fetch its raw records, which date field to filter
// on, and which columns (key/label/align) to show — the render layer below is fully generic.
const REPORT_TYPES = [
  {
    id: 'gate-pass-register',
    category: 'Operations',
    label: 'Gate Pass Register',
    dateField: 'gatePassDate',
    columns: [
      { key: 'gatePassNumber', label: 'Gate Pass #' },
      { key: 'type', label: 'Type' },
      { key: 'materialType', label: 'Material' },
      { key: 'gatePassDate', label: 'Date', fmt: fmtDate },
      { key: 'partyName', label: 'Party' },
      { key: 'warehouseName', label: 'Warehouse' },
      { key: 'totalQuantity', label: 'Qty', fmt: fmtNum, align: 'right' },
      { key: 'totalWeight', label: 'Weight (KG)', fmt: fmtNum, align: 'right' },
      { key: 'status', label: 'Status' },
    ],
    fetch: async () => unwrapList(gatePassService.getAll(0, 500), 'data.content', 'data'),
  },
  {
    id: 'purchases-payables',
    category: 'Finance',
    label: 'Purchases & Payables',
    dateField: 'purchaseDate',
    columns: [
      { key: 'purchaseNumber', label: 'PO #' },
      { key: 'vendorName', label: 'Vendor' },
      { key: 'purchaseDate', label: 'Date', fmt: fmtDate },
      { key: 'dueDate', label: 'Due Date', fmt: fmtDate },
      { key: 'currency', label: 'Currency' },
      { key: 'totalAmount', label: 'Bill Amount', fmt: fmtNum, align: 'right' },
      { key: 'paidAmount', label: 'Amount Paid', fmt: fmtNum, align: 'right' },
      { key: 'outstandingAmount', label: 'Balance Due', fmt: fmtNum, align: 'right' },
      { key: 'status', label: 'Status' },
    ],
    fetch: async () => unwrapList(purchaseService.getAll(), 'data'),
  },
  {
    id: 'invoices-receivables',
    category: 'Finance',
    label: 'Invoices & Receivables',
    dateField: 'invoiceDate',
    columns: [
      { key: 'invoiceNumber', label: 'Invoice #' },
      { key: 'customerName', label: 'Customer' },
      { key: 'type', label: 'Type' },
      { key: 'invoiceDate', label: 'Date', fmt: fmtDate },
      { key: 'dueDate', label: 'Due Date', fmt: fmtDate },
      { key: 'currency', label: 'Currency' },
      { key: 'totalAmount', label: 'Amount', fmt: fmtNum, align: 'right' },
      { key: 'balanceDue', label: 'Balance Due', fmt: fmtNum, align: 'right' },
      { key: 'status', label: 'Status' },
    ],
    fetch: async () => unwrapList(invoiceService.getAll(), 'data.content', 'data'),
  },
  {
    id: 'expense-report',
    category: 'Finance',
    label: 'Expense Report',
    dateField: 'expenseDate',
    columns: [
      { key: 'expenseNumber', label: 'Expense #' },
      { key: 'title', label: 'Title' },
      { key: 'category', label: 'Category' },
      { key: 'expenseDate', label: 'Date', fmt: fmtDate },
      { key: 'totalAmount', label: 'Amount', fmt: fmtNum, align: 'right' },
      { key: 'status', label: 'Status' },
    ],
    fetch: async () => unwrapList(expenseService.getAll(), 'data'),
  },
  {
    id: 'batch-pnl-summary',
    category: 'Operations',
    label: 'Batch P&L Summary',
    dateField: null,
    columns: [
      { key: 'batchCode', label: 'Batch' },
      { key: 'productName', label: 'Product' },
      { key: 'godownName', label: 'Warehouse' },
      { key: 'status', label: 'Status' },
      { key: 'amadTotal', label: 'Sourcing', fmt: fmtNum, align: 'right' },
      { key: 'laborTotal', label: 'Labor', fmt: fmtNum, align: 'right' },
      { key: 'bardanaTotal', label: 'Packaging', fmt: fmtNum, align: 'right' },
      { key: 'totalCost', label: 'Net Total Cost', fmt: fmtNum, align: 'right' },
    ],
    fetch: async () => {
      const batches = await unwrapList(batchService.getAll(), 'data');
      const withPnl = await Promise.all(batches.map(async b => {
        try {
          const pnlRes = await batchService.getPnl(b.id);
          const pnl = pnlRes?.data || pnlRes || {};
          return { ...b, ...pnl };
        } catch {
          return b;
        }
      }));
      return withPnl;
    },
  },
  {
    id: 'container-payment-aging',
    category: 'Trade',
    label: 'Container Payment Aging',
    dateField: 'gdDate',
    columns: [
      { key: 'containerNumber', label: 'Container No.' },
      { key: 'gdNumber', label: 'GD Number' },
      { key: 'gdDate', label: 'GD Date', fmt: fmtDate },
      { key: 'consigneeName', label: 'Consignee' },
      { key: 'countryName', label: 'Country' },
      { key: 'fobValue', label: 'FOB Value', fmt: fmtNum, align: 'right' },
      { key: 'paymentReceived', label: 'Payment', fmt: v => v ? 'Received' : 'Outstanding' },
      { key: 'agingDays', label: 'Aging (days)', fmt: v => v == null ? '' : (v > 0 ? `+${v}` : `${v}`), align: 'right' },
    ],
    fetch: async () => unwrapList(containerAgingService.getAll(), 'data'),
  },
];

const CATEGORY_ORDER = ['Operations', 'Finance', 'Trade'];

// ─────────────────────── Custom Report field catalog ───────────────────────
// The six presets above only show the handful of columns whoever built each report thought
// most people want. "Any report I want" means letting the user pick from the FULL field set
// each data source actually has — this is that full set, one entry per REPORT_TYPES.id. Keys
// match the real API field names (pulled from the backend DTOs, not guessed), so picking a
// field here always resolves to real data, never a blank column.
const CUSTOM_FIELD_CATALOG = {
  'gate-pass-register': [
    { key: 'gatePassNumber', label: 'Gate Pass #' },
    { key: 'type', label: 'Type' },
    { key: 'materialType', label: 'Material Type' },
    { key: 'gatePassDate', label: 'Date', fmt: fmtDate },
    { key: 'warehouseName', label: 'Warehouse' },
    { key: 'partyName', label: 'Party Name' },
    { key: 'partyType', label: 'Party Type' },
    { key: 'vehicleNumber', label: 'Vehicle #' },
    { key: 'driverName', label: 'Driver Name' },
    { key: 'driverContact', label: 'Driver Contact' },
    { key: 'containerNumber', label: 'Container #' },
    { key: 'totalQuantity', label: 'Qty', fmt: fmtNum, align: 'right' },
    { key: 'totalWeight', label: 'Weight (KG)', fmt: fmtNum, align: 'right' },
    { key: 'status', label: 'Status' },
    { key: 'batchCode', label: 'Batch' },
    { key: 'linkedPurchaseNumber', label: 'Linked Purchase #' },
    { key: 'departmentName', label: 'Department' },
    { key: 'createdByName', label: 'Created By' },
    { key: 'issuedByName', label: 'Issued By' },
    { key: 'issuedAt', label: 'Issued At', fmt: fmtDate },
    { key: 'notes', label: 'Notes' },
  ],
  'purchases-payables': [
    { key: 'purchaseNumber', label: 'PO #' },
    { key: 'vendorName', label: 'Vendor' },
    { key: 'purchaseDate', label: 'Date', fmt: fmtDate },
    { key: 'dueDate', label: 'Due Date', fmt: fmtDate },
    { key: 'currency', label: 'Currency' },
    { key: 'totalAmount', label: 'Bill Amount', fmt: fmtNum, align: 'right' },
    { key: 'serviceCharges', label: 'Service Charges', fmt: fmtNum, align: 'right' },
    { key: 'laborCost', label: 'Labor Cost', fmt: fmtNum, align: 'right' },
    { key: 'paidAmount', label: 'Amount Paid', fmt: fmtNum, align: 'right' },
    { key: 'outstandingAmount', label: 'Balance Due', fmt: fmtNum, align: 'right' },
    { key: 'status', label: 'Status' },
    { key: 'referenceNumber', label: 'Reference #' },
    { key: 'vendorInvoiceNumber', label: 'Vendor Invoice #' },
    { key: 'linkedGatePassNumber', label: 'Linked Gate Pass #' },
    { key: 'notes', label: 'Notes' },
  ],
  'invoices-receivables': [
    { key: 'invoiceNumber', label: 'Invoice #' },
    { key: 'invoiceType', label: 'Invoice Type' },
    { key: 'customerName', label: 'Customer' },
    { key: 'customerEmail', label: 'Customer Email' },
    { key: 'invoiceDate', label: 'Date', fmt: fmtDate },
    { key: 'dueDate', label: 'Due Date', fmt: fmtDate },
    { key: 'currency', label: 'Currency' },
    { key: 'subtotal', label: 'Subtotal', fmt: fmtNum, align: 'right' },
    { key: 'taxAmount', label: 'Tax', fmt: fmtNum, align: 'right' },
    { key: 'discountAmount', label: 'Discount', fmt: fmtNum, align: 'right' },
    { key: 'totalAmount', label: 'Total Amount', fmt: fmtNum, align: 'right' },
    { key: 'amountPaid', label: 'Amount Paid', fmt: fmtNum, align: 'right' },
    { key: 'balanceDue', label: 'Balance Due', fmt: fmtNum, align: 'right' },
    { key: 'salesType', label: 'Sales Type' },
    { key: 'priceType', label: 'Price Type' },
    { key: 'status', label: 'Status' },
    { key: 'poNumber', label: 'PO #' },
    { key: 'referenceNumber', label: 'Reference #' },
    { key: 'overdue', label: 'Overdue?', fmt: v => v ? 'Yes' : 'No' },
    { key: 'daysOverdue', label: 'Days Overdue', fmt: fmtNum, align: 'right' },
    { key: 'sentAt', label: 'Sent At', fmt: fmtDate },
    { key: 'paidAt', label: 'Paid At', fmt: fmtDate },
    { key: 'notes', label: 'Notes' },
  ],
  'expense-report': [
    { key: 'expenseNumber', label: 'Expense #' },
    { key: 'title', label: 'Title' },
    { key: 'category', label: 'Category' },
    { key: 'subCategory', label: 'Sub-Category' },
    { key: 'description', label: 'Description' },
    { key: 'expenseDate', label: 'Date', fmt: fmtDate },
    { key: 'amount', label: 'Amount', fmt: fmtNum, align: 'right' },
    { key: 'taxAmount', label: 'Tax', fmt: fmtNum, align: 'right' },
    { key: 'totalAmount', label: 'Total Amount', fmt: fmtNum, align: 'right' },
    { key: 'currency', label: 'Currency' },
    { key: 'paymentMethod', label: 'Payment Method' },
    { key: 'vendorName', label: 'Vendor' },
    { key: 'userName', label: 'Recorded By' },
    { key: 'status', label: 'Status' },
    { key: 'referenceNumber', label: 'Reference #' },
    { key: 'isBillable', label: 'Billable?', fmt: v => v ? 'Yes' : 'No' },
    { key: 'isReimbursable', label: 'Reimbursable?', fmt: v => v ? 'Yes' : 'No' },
    { key: 'rejectionReason', label: 'Rejection Reason' },
    { key: 'notes', label: 'Notes' },
  ],
  'batch-pnl-summary': [
    { key: 'batchCode', label: 'Batch' },
    { key: 'productName', label: 'Product' },
    { key: 'godownName', label: 'Warehouse' },
    { key: 'status', label: 'Status' },
    { key: 'amadWeightKg', label: 'Sourced Weight (KG)', fmt: fmtNum, align: 'right' },
    { key: 'amadTotal', label: 'Sourcing Cost', fmt: fmtNum, align: 'right' },
    { key: 'avgSourceCostPerKg', label: 'Avg Source Cost/KG', fmt: fmtNum, align: 'right' },
    { key: 'deductWeightKg', label: 'Deducted Weight (KG)', fmt: fmtNum, align: 'right' },
    { key: 'deductRecovery', label: 'Deduction Recovery', fmt: fmtNum, align: 'right' },
    { key: 'wastageWeightKg', label: 'Wastage Weight (KG)', fmt: fmtNum, align: 'right' },
    { key: 'wastageLossPkr', label: 'Wastage Loss', fmt: fmtNum, align: 'right' },
    { key: 'laborTotal', label: 'Labor Cost', fmt: fmtNum, align: 'right' },
    { key: 'bardanaTotal', label: 'Packaging Cost', fmt: fmtNum, align: 'right' },
    { key: 'shipmentCostTotal', label: 'Shipment Cost', fmt: fmtNum, align: 'right' },
    { key: 'totalCost', label: 'Net Total Cost', fmt: fmtNum, align: 'right' },
    { key: 'netWeightKg', label: 'Net Weight (KG)', fmt: fmtNum, align: 'right' },
    { key: 'exportWeightKg', label: 'Exported Weight (KG)', fmt: fmtNum, align: 'right' },
    { key: 'shrinkageKg', label: 'Shrinkage (KG)', fmt: fmtNum, align: 'right' },
    { key: 'costPerKgExported', label: 'Cost / KG Exported', fmt: fmtNum, align: 'right' },
    { key: 'totalRevenuePkr', label: 'Total Revenue', fmt: fmtNum, align: 'right' },
    { key: 'profitPkr', label: 'Gross Profit', fmt: fmtNum, align: 'right' },
    { key: 'revenuePerKg', label: 'Revenue / KG', fmt: fmtNum, align: 'right' },
    { key: 'marginPerKg', label: 'Margin / KG', fmt: fmtNum, align: 'right' },
  ],
  'container-payment-aging': [
    { key: 'containerNumber', label: 'Container No.' },
    { key: 'gdNumber', label: 'GD Number' },
    { key: 'gdDate', label: 'GD Date', fmt: fmtDate },
    { key: 'consigneeName', label: 'Consignee' },
    { key: 'countryName', label: 'Country' },
    { key: 'fobValue', label: 'FOB Value', fmt: fmtNum, align: 'right' },
    { key: 'paymentReceived', label: 'Payment', fmt: v => v ? 'Received' : 'Outstanding' },
    { key: 'agingDays', label: 'Aging (days)', fmt: v => v == null ? '' : (v > 0 ? `+${v}` : `${v}`), align: 'right' },
  ],
};

export default function ReportsManagement() {
  const [selectedId, setSelectedId] = useState(REPORT_TYPES[0].id);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [rows, setRows] = useState(null); // null = not generated yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Custom Report Builder: same six data sources as the presets above, but with the full field
  // catalog instead of the preset's fixed column list — "pick any columns you want" (2026-08-20).
  const [customMode, setCustomMode] = useState(false);
  const [selectedColumnKeys, setSelectedColumnKeys] = useState(null); // null = use preset defaults

  const report = REPORT_TYPES.find(r => r.id === selectedId);
  const catalog = CUSTOM_FIELD_CATALOG[selectedId] || report.columns;
  const effectiveColumns = customMode
    ? catalog.filter(f => (selectedColumnKeys || report.columns.map(c => c.key)).includes(f.key))
    : report.columns;

  const grouped = useMemo(() => {
    const map = {};
    for (const r of REPORT_TYPES) {
      if (!map[r.category]) map[r.category] = [];
      map[r.category].push(r);
    }
    return map;
  }, []);

  const selectReport = (id) => {
    setSelectedId(id);
    setRows(null);
    setError('');
    // Reset column selection to that report's own defaults, not the previous report's keys.
    const nextReport = REPORT_TYPES.find(r => r.id === id);
    setSelectedColumnKeys(nextReport.columns.map(c => c.key));
  };

  const toggleColumn = (key) => {
    setSelectedColumnKeys(prev => {
      const base = prev || report.columns.map(c => c.key);
      return base.includes(key) ? base.filter(k => k !== key) : [...base, key];
    });
  };

  const generate = async () => {
    if (customMode && effectiveColumns.length === 0) {
      setError('Pick at least one column before generating.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      let data = await report.fetch();
      if (report.dateField && (dateFrom || dateTo)) {
        data = data.filter(rec => {
          const d = toDateOnly(rec[report.dateField]);
          if (!d) return false;
          if (dateFrom && d < dateFrom) return false;
          if (dateTo && d > dateTo) return false;
          return true;
        });
      }
      setRows(data);
    } catch (e) {
      setError(e.message || 'Failed to generate report');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const cellValue = (col, rec) => {
    const raw = rec[col.key];
    return col.fmt ? col.fmt(raw) : (raw ?? '');
  };

  // {headers, matrix} for <CsvActions> — Preview renders this same shape in a table before
  // Download writes it to disk (2026-09-14, was download-only before).
  const buildReportCsvRows = () => {
    if (!rows) return { headers: [], matrix: [] };
    return {
      headers: effectiveColumns.map(c => c.label),
      matrix: rows.map(rec => effectiveColumns.map(c => cellValue(c, rec))),
    };
  };

  // Fetches the report PDF blob — used by <PdfActions> for Preview + Download, added
  // 2026-09-14. This endpoint is POST-with-body (not a plain GET url), so PdfActions takes a
  // caller-supplied fetchBlob function rather than assuming a URL it can GET itself.
  const fetchReportPdfBlob = async () => {
    const subtitleParts = [];
    if (dateFrom) subtitleParts.push(`From ${fmtDate(dateFrom)}`);
    if (dateTo) subtitleParts.push(`To ${fmtDate(dateTo)}`);
    const body = {
      title: customMode ? `Custom: ${report.label}` : report.label,
      subtitle: subtitleParts.join(' — ') || undefined,
      columns: effectiveColumns.map(c => c.label),
      rows: rows.map(rec => effectiveColumns.map(c => String(cellValue(c, rec)))),
    };
    const res = await fetch(`${API_BASE}/reports/generate-pdf`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('Failed to generate PDF');
    return res.blob();
  };

  return (
    <div className="gd-page">
      <div className="gd-header">
        <div>
          <h1 className="gd-title">Reports</h1>
          <p className="gd-subtitle">Generate reports across every part of the system — gate passes, batches, purchases, invoices, expenses, container aging</p>
        </div>
        <div style={{ display: 'flex', gap: 4, background: 'var(--color-neutral-100)', padding: 4, borderRadius: 10 }}>
          <button
            onClick={() => setCustomMode(false)}
            style={{
              padding: '7px 16px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: !customMode ? 'var(--color-bg-primary)' : 'transparent', color: !customMode ? 'var(--color-primary)' : 'var(--color-text-secondary)',
              boxShadow: !customMode ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
            }}
          >
            Preset Reports
          </button>
          <button
            onClick={() => { setCustomMode(true); if (!selectedColumnKeys) setSelectedColumnKeys(report.columns.map(c => c.key)); }}
            style={{
              padding: '7px 16px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: customMode ? 'var(--color-bg-primary)' : 'transparent', color: customMode ? 'var(--color-primary)' : 'var(--color-text-secondary)',
              boxShadow: customMode ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
            }}
          >
            Custom Report Builder
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Report picker */}
        <div style={{ flex: '0 0 240px', minWidth: 200 }}>
          {CATEGORY_ORDER.filter(cat => grouped[cat]).map(cat => (
            <div key={cat} style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                {cat}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {grouped[cat].map(r => (
                  <button
                    key={r.id}
                    onClick={() => selectReport(r.id)}
                    style={{
                      textAlign: 'left', padding: '9px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                      fontSize: 13, fontWeight: selectedId === r.id ? 700 : 500,
                      background: selectedId === r.id ? 'var(--color-primary-light)' : 'transparent',
                      color: selectedId === r.id ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                    }}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Filters + results */}
        <div style={{ flex: '1 1 480px', minWidth: 320 }}>
          <div className="gd-content" style={{ padding: 0 }}>
            {customMode && (
              <div style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-medium)', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Columns — {report.label}
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setSelectedColumnKeys(catalog.map(f => f.key))}
                      style={{ fontSize: 12, color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                      Select All
                    </button>
                    <button onClick={() => setSelectedColumnKeys(report.columns.map(c => c.key))}
                      style={{ fontSize: 12, color: 'var(--color-text-secondary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                      Reset to Default
                    </button>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 6 }}>
                  {catalog.map(f => (
                    <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={(selectedColumnKeys || []).includes(f.key)} onChange={() => toggleColumn(f.key)} />
                      {f.label}
                    </label>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 8 }}>
                  {effectiveColumns.length} of {catalog.length} columns selected
                </div>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              {report.dateField ? (
                <>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 4 }}>From</label>
                    <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                      style={{ padding: '7px 10px', border: '1px solid var(--color-border-strong)', borderRadius: 6, fontSize: 13 }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 4 }}>To</label>
                    <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                      style={{ padding: '7px 10px', border: '1px solid var(--color-border-strong)', borderRadius: 6, fontSize: 13 }} />
                  </div>
                </>
              ) : (
                <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)', fontStyle: 'italic' }}>This report isn't date-filtered — it always covers everything.</span>
              )}
              <button className="gd-btn gd-btn-primary" onClick={generate} disabled={loading}>
                {loading ? 'Generating…' : 'Generate Report'}
              </button>
              {rows && rows.length > 0 && (() => {
                const { headers, matrix } = buildReportCsvRows();
                return (
                  <>
                    <CsvActions filename={report.id} headers={headers} matrix={matrix} className="gd-btn gd-btn-secondary" />
                    <PdfActions fetchBlob={fetchReportPdfBlob} filename={`${report.id}.pdf`} label="Export PDF" className="gd-btn gd-btn-secondary" />
                  </>
                );
              })()}
            </div>

            {error && <div className="gd-alert gd-alert-error" style={{ marginBottom: 14 }}>{error}</div>}

            {rows === null ? (
              <div className="gd-empty">
                <div className="gd-empty-icon">—</div>
                <div className="gd-empty-title">{report.label}</div>
                <div className="gd-empty-sub">Set your filters (if any) and click Generate Report.</div>
              </div>
            ) : rows.length === 0 ? (
              <div className="gd-empty">
                <div className="gd-empty-icon">—</div>
                <div className="gd-empty-title">No Data</div>
                <div className="gd-empty-sub">Nothing matches this report's filters.</div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 8 }}>{rows.length.toLocaleString()} row{rows.length === 1 ? '' : 's'}</div>
                <div className="gd-table-wrap">
                  <table className="gd-table">
                    <thead>
                      <tr>
                        {effectiveColumns.map(c => (
                          <th key={c.key} style={c.align === 'right' ? { textAlign: 'right' } : undefined}>{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((rec, i) => (
                        <tr key={rec.id ?? i}>
                          {effectiveColumns.map(c => (
                            <td key={c.key} style={c.align === 'right' ? { textAlign: 'right' } : undefined}>
                              {String(cellValue(c, rec) ?? '') || '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
